import classNames from "classnames";
import _ from "lodash";
import { twMerge } from "tailwind-merge";
import data from "../models.json";
import {
  customFieldNames,
  customTitles,
  dropdownFields,
} from "./customisation";
import { useAtomValue } from "jotai";
import { modelDataAtom } from "../atoms";
import { SetStateAction, useEffect, useState } from "react";
import apiAgent from "../api/apiAgent";
import { tMsg } from "../components/ToastNotifications";
/**
 * delay function for better visual response
 * @param ms milliseconds
 * @returns
 */
export const pause = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * LWfn - Ensures the last input tailwind classes are merged. Can separate tailwind classes conditionally
 */
export const mergeClass = (...args: classNames.ArgumentArray) =>
  twMerge(classNames(args));

/**
 * LWfn - Get [from, id] params from URL Search Params as [string, string] or undefined
 */
export const useOriginParams = () => {
  const qryParams = new URLSearchParams(window.location.search);
  const fromModel = qryParams.get("from");
  const fromId = qryParams.get("id");
  const params = fromModel && fromId ? `?from=${fromModel}&id=${fromId}` : ``;
  return [fromModel, fromId, params];
};

/**
 * LWfn - Check if model has sortid
 */
export const useCheckSortable = (modelName: string) => {
  const modelData = useAtomValue(modelDataAtom);
  const findModel = modelData.find((md) => md.name === modelName);
  return _.has(findModel?.fields, "sort_id");
};

/**
 * Capitalizes each word in a string
 * @param input string to capitalize
 * @returns
 */
export const capitalizeAllWords = (input: string) => {
  return _.toLower(input)
    .split(" ")
    .map((w) => _.capitalize(w))
    .join(" ");
};

/**
 * Removes HTML tags from string
 * @param str String to remove tags from
 * @returns
 */
export const removeTags = (str: string) => {
  return str.replace(/(<([^>]+)>)/gi, "");
};

/**
 * LWfn - Check Field Name against 'customFieldNames' constant.
 * returns [displayName:string, hint:string, required:boolean, accepts:string|undefined]
 * This list can be modified
 * @param {string} original is the name of the field
 */
export const checkFieldName = (
  original: string,
  modelName?: string,
): [
  string, // displayName
  string | React.ReactNode | undefined, // hint
  boolean | undefined, // optional
  string | undefined, // accept
  boolean | undefined, // hide
] => {
  const getFieldDetails = customFieldNames.find(
    (field) => field.original === original,
  );

  const checkModel = getFieldDetails?.modelSpecific
    ? getFieldDetails?.modelSpecific.find((m) => m.modelName === modelName)
    : undefined;

  const displayName =
    checkModel?.display ||
    getFieldDetails?.display ||
    _.upperFirst(original.replaceAll("_", " "));
  const hint = checkModel?.hint || getFieldDetails?.hint || undefined;
  const accept = checkModel?.accept || getFieldDetails?.accept || undefined;
  const optional = getBools(
    [checkModel?.optional, getFieldDetails?.optional],
    true,
  );
  const hide = getBools([checkModel?.hide, getFieldDetails?.hide], false);

  return [displayName, hint, !optional, accept, hide];
};

/**
 * LWfn - Check Titles against 'customTitles' constant.
 * This list can be modified
 * @param {string} original is the name of the field
 */
export const checkTitle = (original: string): string => {
  if (!original) {
    console.log("Error in title:", original);
    return "Title error";
  }
  const checkName = customTitles.find((field) => field.original === original);
  const displayName =
    checkName?.display || capitalizeAllWords(original.replaceAll("_", " "));
  return displayName;
};

/**
 * TODO: Review if this is necessary anymore, we now have `requiredFields` on the backend which can store correct field order
 * LWfn - Add order to model schema for forms. Compares data from /data/models to model.json in src folder
 * @param modelData accept json object from /data/models
 * @returns
 */
export const transformModelSchema = (modelData: any[]) => {
  const transformedData = modelData.map((model) => {
    // Match model name
    const modelName = model.name;
    const staticModel = data.find((model) => model.name === modelName);
    if (!staticModel) return model;

    // Get keys as ordered in local models.json
    const staticKeys = Object.keys(staticModel.fields);
    const initFields = staticKeys.reduce((obj, key: string) => {
      // make sure retrieved data has key
      const modelHasKey = key in model.fields;
      return modelHasKey ? { ...obj, [key]: model.fields[key] } : obj;
    }, {});

    // Get remaining keys no in models.json
    const dataKeys = Object.keys(model.fields);
    const combinedFields = dataKeys.reduce((obj, key: string) => {
      // make sure key has not been added to static field
      const staticHasKey = key in initFields;
      return staticHasKey
        ? initFields
        : { ...initFields, [key]: model.fields[key] };
    }, initFields);

    // TODO: Do order of relationships?

    return {
      ...model,
      fields: combinedFields,
    };
  });

  return transformedData;
};

/**
 * Check if string is a valid email
 * @param email string
 * @returns
 */
export const isValidEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email);
};

/**
 * Convert timezone string to readable format
 * @param str_date timezone string
 * @returns
 */
export const parseDate = (str_date: string) => {
  return new Date(Date.parse(str_date)).toLocaleString();
};

/**
 * Checks through an array of booleans and returns the first one to be not undefined
 * @param array array of possible booleans
 * @param defaultAnswer default answer if all are undefined
 * @returns
 */
export const getBools = (
  array: any[],
  defaultAnswer?: boolean,
): boolean | undefined => {
  const answer = array.find((item) => _.isBoolean(item));
  if (_.isBoolean(answer)) return answer;
  return defaultAnswer;
};

/**
 * Helper to compare hardcoded fields against schema
 * @param fields
 * @param schema
 * @returns
 */
export const reduceFieldsAgainstSchema = (fields: any[], schema: any[]) => {
  return fields.reduce((arr: any[], field) => {
    const getSchemaItem = schema.find(
      (schemaItem) => schemaItem.name === field.name,
    );
    if (!getSchemaItem) return arr;
    return [
      ...arr,
      {
        ...field,
        ...getSchemaItem,
      },
    ];
  }, []);
};

/**
 * Use the provided function to pull the data from a table called "mediaItems" that has a "thumbUrl" or "fullImageUrl" column.
 * Will return undefined if any of the above does not exist
 * @returns (modelName:string, ownerId:number, setImageSrc:SetStateAction)
 */
export const useFetchThumbFromMediaItem = () => {
  /**
   * (modelName:string, ownerId:number, setImageSrc:SetStateAction)
   */
  return async (
    modelName: string,
    ownerId: number,
    setThumbSrc: React.Dispatch<SetStateAction<string | undefined>>,
  ) => {
    if (modelName === "mediaitems") return;
    try {
      const response = await apiAgent.DataType.getAll("mediaitems");
      const allItems = response?.data || [];
      const ownerKey = `owner_${modelName}_id`;
      const getFirst = allItems.find(
        (mi: any) => _.has(mi, ownerKey) && mi[ownerKey] === ownerId,
      );
      if (getFirst)
        setThumbSrc(getFirst.thumburl || getFirst.fullimageurl || undefined);
    } catch (err) {
      console.log(err);
    }
  };
};

export const useFetchThumbListFromMediaItem = (modelName: string) => {
  const [mediaItems, setMediaItems] = useState<any[]>([]);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const modelData = useAtomValue(modelDataAtom);

  const validateAndFetchMediaItemList = async () => {
    // First validate if possible to get media items
    if (isValid !== null) return;
    if (!modelData || modelData.length < 1) return;
    const modelSet = modelData.find((md) => md.name === modelName);
    if (!modelSet) {
      setIsValid(false);
      return;
    }
    const hasMediaItems = modelSet.relationships.find(
      (r) => r.related_to === "mediaitems",
    );
    if (!hasMediaItems) {
      setIsValid(false);
      return;
    }
    const noNeedForMediaItems = _.includes(modelSet.fields, [
      "thumbnail_src",
      "full_resolution_image",
      "thumbnail",
      "video_thumbnail",
    ]);
    if (noNeedForMediaItems) {
      setIsValid(false);
      return;
    }

    // then try to get media items
    try {
      const response = await apiAgent.DataType.getAll("mediaitems");
      setMediaItems(response.data);
    } catch (err) {
      console.log("Failed to get media items for thumbs");
    } finally {
      setIsValid(true);
    }
  };

  useEffect(() => {
    validateAndFetchMediaItemList();
  }, [modelName, modelData]);

  const addThumbsToData = (data: readonly any[]) =>
    data.map((item) => {
      const ownerKey = `owner_${modelName}_id`;
      const getFirst = mediaItems.find(
        (mi: any) => _.has(mi, ownerKey) && mi[ownerKey] === item.id,
      );
      const thumbnail_src =
        getFirst?.thumbnail_src || getFirst?.full_resolution_image;
      return {
        ...item,
        thumbnail_src,
      };
    });

  // return isValid ? addThumbsToData : null;
  return {
    isValid,
    addThumbsToData,
  };
};

/**
 * Get the display label of a dropdown option
 * @param field name of field
 * @param value value
 * @returns label of value
 */
export const getDropdownLabel = (
  model: string,
  field: string,
  value: string | undefined,
) => {
  if (!value) return value;
  const dropdownData = dropdownFields.find(
    (df) => df.model === model && df.field === field,
  );
  if (!dropdownData) return value;
  else return dropdownData.options.find((o) => o.value === value)?.label;
};

interface IModelData {
  model: string;
  data: any[] | undefined;
}

const modelsToCheck = [
  "video_player",
  "personal_story",
  "showcase_label",
  "digital_label",
];

/**
 * Find any items attached to an exhibit
 * @returns fetchAssignedExhibit(exhibitTitle:string, setLoading?: for loading state)
 */
export const useCheckExhibitAssigned = (models: string[]) => {
  const bIsUsed = _.includes(models, "exhibit");
  const [checkData, setCheckData] = useState<IModelData[] | null>(null); //
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (bIsUsed) fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      const promises = modelsToCheck.map((model) =>
        apiAgent.DataType.getAll(model),
      );
      const responses = await Promise.all(promises);
      const parsedData: IModelData[] = responses.map((response, index) => {
        return {
          model: modelsToCheck[index],
          data: response?.data,
        };
      });
      setCheckData(parsedData);
    } catch (err) {
      console.log(err);
      tMsg.warning("Could not load assignments for exhibits");
    } finally {
      setIsLoading(false);
    }
  };

  const checkExhibit = (exhibitTitle: string) => {
    if (!checkData) return null;
    const assignment = checkData.find((model) => {
      return model.data?.find((item) => item.exhibit === exhibitTitle);
    });
    return assignment;
  };

  return bIsUsed ? checkExhibit : null;
};

/**
 * Replaces '_' with spaces to display model names nicely
 * @param model
 * @returns string
 */
export const parseModelToReadable = (model: string) =>
  _.capitalize(model).replaceAll("_", " ");

/**
 * Get owner item from data
 * @param item itemData including owner_<model>_id fields
 * @returns owner item
 */
export const getOwnerFromData = async (item: any) => {
  const ownerInfo = Object.keys(item).reduce(
    (obj: null | { id: number; model: string }, key: string) => {
      if (obj !== null) return obj;
      if (_.startsWith(key, "owner_") && item[key]) {
        const getId = item[key];
        const getModel = key.replace("owner_", "").replace("_id", "");
        return { model: getModel, id: getId };
      }
      return null;
    },
    null,
  );

  if (!ownerInfo)
    return {
      ownerItem: undefined,
      ownerInfo,
    };

  try {
    const ownerResponse = await apiAgent.DataType.get(
      ownerInfo.model,
      ownerInfo.id,
    );
    const ownerItem = ownerResponse.data[0];

    return {
      ownerItem,
      ownerInfo,
    };
  } catch (err) {
    console.log("Unable to find owner");
    return {
      ownerItem: undefined,
      ownerInfo,
    };
  }
};

/**
 * Add owner item to item data
 * @param item itemData including owner_<model>_id fields
 * @returns owner item
 */
export const addOwnerToItem = async (item: any) => {
  const ownerInfo = Object.keys(item).reduce(
    (obj: null | { id: number; model: string }, key: string) => {
      if (obj !== null) return obj;
      if (_.startsWith(key, "owner_") && item[key]) {
        const getId = item[key];
        const getModel = key.replace("owner_", "").replace("_id", "");
        return { model: getModel, id: getId };
      }
      return null;
    },
    null,
  );

  if (!ownerInfo) return item;

  try {
    const ownerResponse = await apiAgent.DataType.get(
      ownerInfo.model,
      ownerInfo.id,
    );
    const ownerItem = ownerResponse.data[0];

    return { ...item, ownerItem };
  } catch (err) {
    console.log("Unable to find owner");
    return item;
  }
};
